/**
 * Reviewer Console "Generate Report" — new entry point.
 *
 * Pages built so far:
 *   • Page 1 — Cover (renderCoverPage)
 *   • Page 2 — Assessment Profile (renderAssessmentProfile)
 *   • Page 3 — Executive Summary (renderExecutiveSummary)
 *   • Page 4 — Section 2: NIST CSF 2.0 Functional Ratings
 *               (renderNistFunctionRatings)
 *
 * Pages 5+ are intentionally NOT generated by this function yet.
 */
import jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";

import { renderCoverPage } from "./renderCoverPage";
import { renderAssessmentProfile } from "./renderAssessmentProfile";
import { renderExecutiveSummary } from "./renderExecutiveSummary";
import { OPERATING_CONTEXT_RENDERER_VERSION, renderOperatingContext } from "./renderOperatingContext";
import { buildTelecomContextBlock } from "@/reports/telecom/operatingContext";
import {
  applyReportWatermark,
  resolveReportWatermark,
  watermarkFilename,
} from "@/reports/reportWatermark";

import {
  buildVendorExposureNarrative,
  type VendorExposureRow,
} from "@/reports/telecom/vendorExposure";
import { renderNistFunctionRatings } from "./renderNistFunctionRatings";
import { renderTechnicalValidation } from "./renderTechnicalValidation";
import type { TvfRow } from "@/reports/telecom/technicalValidation";
import { renderCsvDomainRatings } from "./renderCsvDomainRatings";
import { renderTopFindings } from "./renderTopFindings";
import type { EvidenceAppendixRow } from "./renderEvidenceAppendix";

import { renderDomainResults } from "./renderDomainResults";
import { renderDataClassification, type DataClassificationRow } from "./renderDataClassification";
import { renderRiskDistribution, type RiskDistributionMetrics } from "./renderRiskDistribution";
import { renderRemediationRoadmap } from "./renderRemediationRoadmap";
import { renderDetailedFindingsAppendix } from "./renderDetailedFindingsAppendix";
import { renderTelecomResults, type TelecomReportBundle } from "./renderTelecomResults";
import { buildTelecomExecutiveSummary } from "@/reports/telecom/infrastructureNarratives";
import { loadCsvProLogo, loadClientLogo } from "./loadLogo";
import { PAGE_MARGIN } from "./theme";

import {
  bandFromAverage,
  buildFunctionVisualRows,
  buildPriorityFocusAreas,
  type FindingRow,
  type MaturityBand,
} from "@/reports/telecom/cyberResilienceSummary";

export interface GenerateReviewerReportArgs {
  assessmentId: string;
  /** Optional — looked up from the assessment row when omitted. */
  orgId?: string;
  /** Best-known display name for the client org. */
  orgName: string;
  /** When "preview", returns a PDF blob for the in-app preview modal. */
  mode?: "download" | "preview";
}

export interface GenerateReviewerReportResult {
  filename: string;
  /** PDF data URI (data:application/pdf;base64,...) when mode === "preview". */
  dataUrl?: string;
}

export async function generateReviewerReport(
  args: GenerateReviewerReportArgs,
): Promise<GenerateReviewerReportResult> {
  const sb = supabase as any;

  // Resolve org_id if not supplied (e.g. admin entered via /reviewer/:id deep link).
  let orgId = args.orgId ?? "";
  if (!orgId) {
    const { data: detailRows, error: detailError } = await sb.rpc("reviewer_get_assessment_detail", {
      p_assessment_id: args.assessmentId,
    });
    if (detailError) console.warn("[reviewer-pdf] client logo: assessment org lookup failed", detailError);
    const detail = Array.isArray(detailRows) ? detailRows[0] : detailRows;
    orgId = detail?.org_id ?? "";
  }

  // Fetch the data all pages need.
  const [profileRes, execSummaryRes, nistRes, csvRes, findingsRes, roadmapRes, breakdownRes, orgLogoRes, scopeRes, telecomBundleRes, infraCoverageRes, vendorExposureRes, tvfRes, evidenceAppendixRes, controlResultsRes, dataClassificationRes, progressRes] = await Promise.all([
    sb.rpc("reviewer_get_assessment_profile", { p_assessment_id: args.assessmentId }),
    sb.rpc("reviewer_get_executive_summary", { p_assessment_id: args.assessmentId }),
    sb.rpc("calculate_nist_function_scores", { p_assessment_id: args.assessmentId }),
    sb.rpc("calculate_csv_domain_scores", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    sb.rpc("reviewer_get_findings", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    sb.rpc("reviewer_get_roadmap", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    sb.rpc("reviewer_get_csv_domain_implementation_breakdown", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    orgId
      ? sb.rpc("get_assessment_org_logo", { p_org_id: orgId }).then(
          (r: any) => r,
          (error: any) => ({ data: null, error }),
        )
      : Promise.resolve({ data: null, error: { message: "Assessment org_id not found" } }),
    // Phase 1 scope — informational only.
    sb.rpc("reviewer_get_infrastructure_scope", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Phase 2.4 — Telecom report bundle (report-only, scoring-neutral).
    sb.rpc("reviewer_get_telecom_report_bundle", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Phase 2.12 — Infrastructure Security partial-coverage detection (booleans only).
    sb.rpc("reviewer_get_infrastructure_assessment_coverage", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Phase 2.14 — Telecom Vendor Exposure (reviewer-managed).
    sb.rpc("reviewer_list_telecom_vendor_exposure", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Phase 2.15 — Technical Validation Findings (reviewer-managed; non-scoring).
    sb.rpc("reviewer_list_technical_validation_findings", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Appendix B — Evidence Traceability (justifications + secure links).
    sb.rpc("get_assessment_finding_evidence", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Section 05 — Per-control reviewer results (status, notes, recommendation).
    sb.rpc("reviewer_get_control_results", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Section 06 — Data Classification & Information Protection.
    sb.rpc("reviewer_get_data_classification", { p_assessment_id: args.assessmentId }).then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
    // Section 08 — Risk Distribution & Assessment Statistics (single source: progress summary).
    sb.rpc("reviewer_get_progress_summary").then(
      (r: any) => r,
      (error: any) => ({ data: null, error }),
    ),
  ]);
  const scope = Array.isArray(scopeRes?.data) ? scopeRes.data[0] : scopeRes?.data;
  const infraCoverageRow = Array.isArray(infraCoverageRes?.data) ? infraCoverageRes.data[0] : infraCoverageRes?.data;
  const showInfraCoverageNote = infraCoverageRow?.show_coverage_note === true;

  const profile = Array.isArray(profileRes.data) ? profileRes.data[0] : profileRes.data;
  const execSummary = Array.isArray(execSummaryRes.data) ? execSummaryRes.data[0] : execSummaryRes.data;
  const nistScores = (nistRes.data ?? []) as Array<{
    function_name: string;
    average_score: number | null;
    controls_evaluated?: number | null;
  }>;
  const csvScores = (csvRes?.data ?? []) as Array<{
    domain_name: string;
    average_score: number | null;
    controls_evaluated?: number | null;
    maturity_level?: string | null;
  }>;
  if (csvRes?.error) {
    console.warn("[reviewer-pdf] csv domain ratings: calculate_csv_domain_scores failed", csvRes.error);
  }
  const findingsRaw = (findingsRes?.data ?? []) as any[];
  const findings: FindingRow[] = Array.isArray(findingsRaw)
    ? findingsRaw.map((f) => ({
        control_code: f?.control_code ?? null,
        finding_title: f?.finding_title ?? null,
        risk_level: f?.risk_level ?? null,
        nist_function: f?.nist_function ?? null,
      }))
    : [];
  if (findingsRes?.error) {
    console.warn("[reviewer-pdf] priority focus: reviewer_get_findings failed", findingsRes.error);
  }
  const roadmapRaw = (roadmapRes?.data ?? []) as any[];
  if (roadmapRes?.error) {
    console.warn("[reviewer-pdf] domain results: reviewer_get_roadmap failed", roadmapRes.error);
  }
  const breakdownRaw = (breakdownRes?.data ?? []) as Array<{
    domain_name: string;
    implemented: number;
    partial: number;
    not_implemented: number;
  }>;
  if (breakdownRes?.error) {
    console.warn(
      "[reviewer-pdf] domain results: reviewer_get_csv_domain_implementation_breakdown failed",
      breakdownRes.error,
    );
  }
  const orgLogoRow = Array.isArray(orgLogoRes?.data) ? orgLogoRes.data[0] : orgLogoRes?.data;
  const rawLogoPath: string | null = orgLogoRow?.logo_url ?? null;
  if (orgLogoRes?.error) {
    console.warn("[reviewer-pdf] client logo: get_assessment_org_logo failed", orgLogoRes.error);
  }
  if (rawLogoPath) {
    console.info("[reviewer-pdf] client logo: get_assessment_org_logo returns a value", { rawLogoPath });
  } else {
    console.warn("[reviewer-pdf] client logo: no logo URL found", { orgId, rpcData: orgLogoRes?.data ?? null });
  }
  // RPC returns a storage path (e.g. "org-assets/abc/logo.png"), not a public URL.
  // Convert via getLogoUrl so the browser can actually fetch it.
  const { getLogoUrl } = await import("@/utils/getLogoUrl");
  const clientLogoUrl: string | null = getLogoUrl(rawLogoPath);
  if (clientLogoUrl) {
    console.info("[reviewer-pdf] client logo: getLogoUrl() returns a usable URL", { clientLogoUrl });
  }
  if (rawLogoPath && !clientLogoUrl) {
    console.warn("[reviewer-pdf] client logo: getLogoUrl returned null", { rawLogoPath });
  }

  // Resolve effective band: reviewer override (cyber_resilience_score) wins;
  // otherwise compute from NIST function averages. No numeric exposure.
  const band = resolveExecutiveBand(execSummary, nistScores);

  const clientOrgName =
    args.orgName?.trim() || profile?.client_organization?.trim() || "Client Organization";

  // Load both logos in parallel; tolerate failures.
  const [csvProLogo, clientLogo] = await Promise.all([
    loadCsvProLogo(),
    loadClientLogo(clientLogoUrl),
  ]);

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  console.info("[reviewer-pdf] renderer marker", {
    operatingContext: OPERATING_CONTEXT_RENDERER_VERSION,
  });

  const methodology = profile?.methodology ?? "NIST CSF 2.0";
  const frameworks: string[] = Array.isArray(profile?.methodology_frameworks)
    ? profile.methodology_frameworks.filter((f: any) => typeof f === "string" && f.trim())
    : [];

  // Phase 2.4 — Telecom report bundle (informational only).
  const telecomBundle = (telecomBundleRes?.data ?? null) as TelecomReportBundle | null;
  if (telecomBundleRes?.error) {
    console.warn("[reviewer-pdf] telecom: reviewer_get_telecom_report_bundle failed", telecomBundleRes.error);
  }
  const telecomCounts = telecomBundle?.counts ?? null;
  const telecomOverview =
    telecomCounts && telecomCounts.controls_evaluated > 0
      ? buildTelecomExecutiveSummary({
          serviceModels: telecomBundle?.scope?.telecom_service_model ?? [],
          modulesAssessed: telecomBundle?.scope?.components_assessed ?? [],
          counts: telecomCounts,
        })
      : null;

  // Page 1
  renderCoverPage(doc, {
    clientOrgName,
    parentOrgName: profile?.parent_organization ?? null,
    sector: profile?.sector ?? null,
    headquarters: profile?.headquarters_location ?? null,
    engagementStart: profile?.engagement_start_date ?? null,
    engagementEnd: profile?.engagement_end_date ?? null,
    conductedBy: profile?.assessment_conducted_by ?? null,
    methodology,
    format: profile?.format ?? null,
    reportDateIso: new Date().toISOString(),
    csvProLogo,
    clientLogo,
  });

  // Phase 2.14 — Vendor Exposure (reviewer-managed; telecom only).
  const vendorExposure = (Array.isArray(vendorExposureRes?.data)
    ? vendorExposureRes.data
    : []) as VendorExposureRow[];
  if (vendorExposureRes?.error) {
    console.warn(
      "[reviewer-pdf] vendor exposure: reviewer_list_telecom_vendor_exposure failed",
      vendorExposureRes.error,
    );
  }
  const vendorExposureNarrative = buildVendorExposureNarrative(vendorExposure);
  const vendorExposureConfirmed = !!profile?.vendor_exposure_confirmed;

  // Page 2 — Section 01: Executive Summary
  // Vendor exposure intentionally omitted here; it is presented in Section 02.
  void vendorExposureNarrative;
  void vendorExposureConfirmed;
  renderExecutiveSummary(doc, {
    narrative: execSummary?.executive_summary ?? null,
    band,
    clientLogo,
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 2,
    telecomOverview,
  });

  // Page 3 — Section 02: Assessment Profile
  renderAssessmentProfile(doc, profile ?? null, {
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 3,
    scope: scope ?? null,
  });

  // Page 4 — Section 02 (cont'd): Operating & Regulatory Context (Phase 2.13)
  const contextBlock = buildTelecomContextBlock({
    orgName: clientOrgName,
    parentOrganization: profile?.parent_organization ?? null,
    headquartersLocation: profile?.headquarters_location ?? null,
    region: profile?.region ?? null,
    sector: profile?.sector ?? null,
    frameworks,
    notes: profile?.operating_context_notes ?? null,
    legacyMethodology: profile?.methodology ?? null,
    serviceModels: Array.isArray(scope?.telecom_service_model) ? scope.telecom_service_model : [],
    componentsPresent: Array.isArray(scope?.components_present) ? scope.components_present : [],
    componentsAssessed: Array.isArray(scope?.components_assessed) ? scope.components_assessed : [],
    confirmed: !!profile?.operating_context_confirmed,
    override: profile?.operating_context_override ?? null,
    overrideEnabled: !!profile?.operating_context_override_enabled,
  });
  renderOperatingContext(doc, contextBlock, {
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 4,
    vendorExposure,
    vendorExposureConfirmed,
  });

  // Page 5 — Section 03: Cyber Resilience & Domain Ratings — NIST half
  const visualRows = buildFunctionVisualRows(nistScores);
  const priorityFocusAreas = buildPriorityFocusAreas(visualRows, findings).slice(0, 4);
  renderNistFunctionRatings(doc, {
    scores: nistScores,
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 5,
    priorityFocusAreas,
  });

  // Page 6 — Section 03 (cont'd): CSV Cybersecurity Domain Ratings
  renderCsvDomainRatings(doc, {
    scores: csvScores,
    overallBand: band,
    keyStrengths: execSummary?.key_strengths ?? null,
    keyAreasForImprovement: execSummary?.key_areas_for_improvement ?? null,
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 6,
    showInfrastructureCoverageNote: showInfraCoverageNote,
  });

  // Page 7 — Section 04: Top Findings & Recommendations
  renderTopFindings(doc, {
    findings: findingsRaw as any[],
    overallBand: band,
    clientOrgName,
    methodology,
    frameworks,
    pageNumber: 7,
  });

  // Page 8+ — Section 05: Assessment Results by Cybersecurity Domain
  const controlResultsRaw = (Array.isArray(controlResultsRes?.data)
    ? controlResultsRes.data
    : []) as any[];
  if (controlResultsRes?.error) {
    console.warn(
      "[reviewer-pdf] domain results: reviewer_get_control_results failed",
      controlResultsRes.error,
    );
  }
  const lastDomainPage = renderDomainResults(doc, {
    scores: csvScores,
    roadmap: roadmapRaw as any[],
    breakdown: breakdownRaw,
    controlResults: controlResultsRaw,
    clientOrgName,
    methodology,
    frameworks,
    startPageNumber: 8,
    scope: scope ?? null,
  });

  // Telecommunications Infrastructure Security (informational; appears as
  // a sub-section of Section 05 to keep the canonical numbering 05 → 06 → 07).
  renderTelecomResults(doc, {
    bundle: telecomBundle,
    clientOrgName,
    methodology,
    frameworks,
    startPageNumber: (lastDomainPage ?? 7) + 1,
    sectionNumber: "05B",
  });

  // Section 06 — Data Classification & Information Protection.
  const dataClassificationRows = (Array.isArray(dataClassificationRes?.data)
    ? dataClassificationRes.data
    : []) as DataClassificationRow[];
  if (dataClassificationRes?.error) {
    console.warn(
      "[reviewer-pdf] data classification: reviewer_get_data_classification failed",
      dataClassificationRes.error,
    );
  }
  const lastPageBeforeDataClassification = (doc as any).internal?.getNumberOfPages?.() ?? 1;
  renderDataClassification(doc, {
    rows: dataClassificationRows,
    clientOrgName,
    methodology,
    frameworks,
    startPageNumber: lastPageBeforeDataClassification + 1,
  });

  // Section 07 — Technical Validation Results (reviewer-managed, non-scoring).
  // Skipped automatically when there are no rows.
  const tvfRows = (Array.isArray(tvfRes?.data) ? tvfRes.data : []) as TvfRow[];
  if (tvfRes?.error) {
    console.warn(
      "[reviewer-pdf] technical validation: reviewer_list_technical_validation_findings failed",
      tvfRes.error,
    );
  }
  renderTechnicalValidation(doc, tvfRows, {
    clientOrgName,
    methodology,
    frameworks,
    sectionNumber: "07",
  });

  // Section 08 — Risk Distribution & Assessment Statistics.
  // Source: reviewer_get_progress_summary row matched on this assessment_id.
  // This is the SAME data shown on the Reviewer Console's Executive Summary (Live)
  // — Assessment Statistics + Risk Distribution panels. No legacy recalculation.
  const progressRows = Array.isArray(progressRes?.data) ? progressRes.data : (progressRes?.data ? [progressRes.data] : []);
  if (progressRes?.error) {
    console.warn("[reviewer-pdf] section 08: reviewer_get_progress_summary failed", progressRes.error);
  }
  const progressMatch = (progressRows as any[]).find((r) => r?.assessment_id === args.assessmentId) ?? null;
  const riskMetrics: RiskDistributionMetrics | null = progressMatch
    ? {
        totalControls:    Number(progressMatch.total_questions ?? 0),
        implemented:      Number(progressMatch.implemented_count ?? 0),
        partial:          Number(progressMatch.partial_count ?? 0),
        notImplemented:   Number(progressMatch.not_implemented_count ?? 0),
        totalFindings:    Number(progressMatch.findings_count ?? 0),
        highFindings:     Number(progressMatch.high_risk_findings ?? 0),
        moderateFindings: Number(progressMatch.medium_risk_findings ?? 0),
        lowFindings:
          Number(progressMatch.findings_count ?? 0)
          - Number(progressMatch.high_risk_findings ?? 0)
          - Number(progressMatch.medium_risk_findings ?? 0),
      }
    : null;
  renderRiskDistribution(doc, {
    metrics: riskMetrics,
    clientOrgName,
    methodology,
    frameworks,
    sectionNumber: "08",
    resilienceBand: band,
  });

  // Section 09 — Remediation Roadmap & Recommended Actions.
  // Sourced from reviewer findings (reviewer_get_roadmap); no DB changes.
  const lastPageBeforeRoadmap = (doc as any).internal?.getNumberOfPages?.() ?? 1;
  renderRemediationRoadmap(doc, {
    rows: roadmapRaw as any[],
    clientOrgName,
    methodology,
    frameworks,
    startPageNumber: lastPageBeforeRoadmap + 1,
  });

  // Appendix A — Detailed Findings Justification & Supporting Evidence.
  // Reuses the evidence-traceability rows (justification, observed gap,
  // evidence reviewed, recommended additional evidence) and the roadmap
  // rows for ordering + csv_domain / nist_function / priority / timeframe.
  const evidenceRowsForAppendixA = (Array.isArray(evidenceAppendixRes?.data)
    ? evidenceAppendixRes.data
    : []) as EvidenceAppendixRow[];
  const lastPageBeforeDetailedAppendix = (doc as any).internal?.getNumberOfPages?.() ?? 1;
  renderDetailedFindingsAppendix(doc, {
    evidenceRows: evidenceRowsForAppendixA,
    roadmapRows: roadmapRaw as any[],
    clientOrgName,
    methodology,
    frameworks,
    startPageNumber: lastPageBeforeDetailedAppendix + 1,
  });

  // Appendices B (Evidence Traceability) and C (Infrastructure Scope Notes)
  // intentionally removed per product decision.






  // Final pass: rewrite sequential page numbers in the footer so continuation
  // pages don't repeat the section's start page (e.g. two "Page 4"s for
  // Operating & Regulatory Context). Cover (page 1) is skipped.
  try {
    const internal: any = (doc as any).internal;
    const total: number = internal?.getNumberOfPages?.() ?? 1;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const y = pageH - 28;
    for (let p = 2; p <= total; p++) {
      doc.setPage(p);
      // Clear prior "Page N" text on the right.
      doc.setFillColor(255, 255, 255);
      doc.rect(pageW - PAGE_MARGIN - 120, y - 11, 120, 16, "F");
      doc.setTextColor(110, 110, 110);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const label = `Page ${p}`;
      doc.text(label, pageW - PAGE_MARGIN - doc.getTextWidth(label), y);
    }
  } catch (err) {
    console.warn("[reviewer-pdf] page-number renumber pass failed", err);
  }

  const safeName = clientOrgName.replace(/[^\w\-]+/g, "_");

  // Phase 3.5A — composed assessments carry a server-decided classification.
  // Legacy assessments return { required: false } and stay byte-identical.
  const watermark = await resolveReportWatermark(args.assessmentId);
  applyReportWatermark(doc, watermark);

  const filename = watermarkFilename(`${safeName}_Cybersecurity_Report.pdf`, watermark);


  if (args.mode === "preview") {
    let dataUrl: string | undefined;
    try {
      dataUrl = doc.output("datauristring") as string;
    } catch (err) {
      console.warn("[reviewer-pdf] datauristring generation failed; preview will fall back", err);
    }
    return { filename, dataUrl };
  }

  doc.save(filename);

  return { filename };
}

function resolveExecutiveBand(
  execSummary: any,
  nistScores: Array<{ average_score: number | null; controls_evaluated?: number | null }>,
): MaturityBand | null {
  const overrideOn = !!execSummary?.cyber_resilience_override;
  const overrideScore = execSummary?.cyber_resilience_score;
  if (overrideOn && overrideScore != null && Number.isFinite(Number(overrideScore))) {
    const b = bandFromAverage(Number(overrideScore), 1);
    return b === "Unrated" ? null : (b as MaturityBand);
  }

  const rated = nistScores.filter(
    (s) => Number.isFinite(Number(s.average_score)) && Number(s.controls_evaluated ?? 0) > 0,
  );
  if (rated.length === 0) return null;
  const avg = rated.reduce((a, s) => a + Number(s.average_score), 0) / rated.length;
  const b = bandFromAverage(avg, rated.length);
  return b === "Unrated" ? null : (b as MaturityBand);
}
